'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from '@bridge/utils';
import { Command as CommandPrimitive } from 'cmdk';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { faCheck, faPlus, faSearch, faXmark } from '@icons/classic/regular';

import { Body } from '../typography';
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from './Command';
import { FlexContainer } from './FlexContainer';
import { inputContainer } from './Input';
import { LoadingIndicator } from './LoadingIndicator';
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from './Popover';

import type { FormOption } from '../types';

export interface SearchInputProps
  extends Omit<React.ComponentPropsWithoutRef<'input'>, 'value' | 'onChange' | 'ref' | 'role' | 'type' | 'onBlur'> {
  /**
   * Current selected option value. The displayed label is resolved from
   * `initialOptions` when this value is set.
   */
  value?: string;
  /**
   * Called when the selected value changes — on selection and on clear.
   * Also fires with `""` on each keystroke when `clearValueOnType` is true.
   */
  onChange: (value: string) => void;
  /**
   * When true, typing in the input fires `onChange("")` to drop any prior
   * selection while the user searches. Appropriate for form fields backed
   * by react-hook-form (matches the RHF "field is no longer valid" signal).
   * Leave false in filter / URL-synced contexts where the live value should
   * persist until an explicit new selection or clear.
   */
  clearValueOnType?: boolean;
  /** Called when the clear button is clicked. Useful for cache invalidation. */
  onClear?: () => void;
  /**
   * Async search function. When provided, typing above `minSearchInput`
   * triggers a debounced search; omit to enable static filtering of
   * `initialOptions` only.
   */
  onSearch?: (searchValue: string) => Promise<FormOption[]>;
  /** Optional "add new" affordance rendered at the bottom of results. */
  onAdd?: () => Promise<FormOption>;
  addButtonText?: string;
  initialOptions?: FormOption[] | (() => FormOption[]);
  placeholder?: string;
  searchEmptyMessage?: string;
  debounceMs?: number;
  /** Minimum input length before `onSearch` fires (default: 3). */
  minSearchInput?: number;
  /**
   * When true, calls `onSearch('')` on first focus to populate results
   * before the user types. Useful for dropdowns that should show a first
   * page of results immediately.
   */
  fetchOnOpen?: boolean;
  /**
   * When true, the popover expands beyond the input width to fit content.
   * Default (false) matches the popover width to the input trigger.
   */
  popoverAutoWidth?: boolean;
  /**
   * Render the results popover inline instead of portaling to `document.body`.
   * Set this when the input lives inside a modal Dialog/Sheet, whose scroll-lock
   * otherwise prevents wheel/trackpad scrolling of a portaled results list.
   */
  disablePopoverPortal?: boolean;
  /**
   * Caps the static (no `onSearch`) results rendered at once, since results mount without
   * virtualization. Set it for large option sets; the selected label still resolves from every option.
   */
  maxResults?: number;
}

/**
 * Standalone search combobox with static and async modes. Renders an input
 * that opens a popover of matching results; select one to set `value`.
 *
 * For use inside `DeclarativeForm`, prefer the `searchInput` field variant
 * (`SearchInputField`) which wires this into react-hook-form and renders
 * the standard form label / description / error slots.
 */
export function SearchInput({
  value,
  onChange,
  clearValueOnType = false,
  onClear,
  onSearch,
  onAdd,
  addButtonText,
  initialOptions,
  placeholder = 'Start typing to search...',
  searchEmptyMessage = 'No options found',
  debounceMs = 300,
  minSearchInput = 3,
  fetchOnOpen = false,
  popoverAutoWidth = false,
  disablePopoverPortal = false,
  maxResults,
  disabled,
  className,
  ...inputProps
}: SearchInputProps) {
  const resolvedInitialOptions = useMemo(() => {
    const options = typeof initialOptions === 'function' ? initialOptions() : initialOptions;
    return options ?? [];
  }, [initialOptions]);

  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState<FormOption[]>(() => resolvedInitialOptions.slice(0, maxResults));
  const [isSearching, setIsSearching] = useState(false);
  const prevValueRef = useRef(value);
  const isUserTypingRef = useRef(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Restore the label for an externally-set value (e.g. initial form value
  // or URL hydration) when the matching option is in initialOptions. Also
  // clears the display when the value is externally reset to empty.
  useEffect(() => {
    const prevValue = prevValueRef.current;
    const currentValue = value;

    if (currentValue) {
      const selectedOption = resolvedInitialOptions.find((option) => option.value === currentValue);
      if (selectedOption) {
        setSearchValue(selectedOption.label);
      }
    } else if (prevValue && !currentValue && !isUserTypingRef.current) {
      setSearchValue('');
    }

    isUserTypingRef.current = false;
    prevValueRef.current = currentValue;
  }, [value, resolvedInitialOptions]);

  const filterStaticOptions = useCallback(
    (searchTerm: string) => {
      const query = searchTerm.trim().toLowerCase();
      const matches = query
        ? resolvedInitialOptions.filter(
          (option) => option.label.toLowerCase().includes(query) || option.value.toLowerCase().includes(query),
        )
        : resolvedInitialOptions;
      return matches.slice(0, maxResults);
    },
    [resolvedInitialOptions, maxResults],
  );

  // Keep searchResults in sync when the available options change
  useEffect(() => {
    if (!onSearch) {
      setSearchResults(filterStaticOptions(searchValue));
      return;
    }
    if (searchValue.trim().length < minSearchInput && !fetchOnOpen) {
      setSearchResults(resolvedInitialOptions);
    }
  }, [resolvedInitialOptions, onSearch, filterStaticOptions, searchValue, minSearchInput, fetchOnOpen]);

  const performSearch = useCallback(
    async (searchTerm: string) => {
      if (value && searchTerm.toLowerCase() === value.toLowerCase()) return;
      if (!onSearch) return;

      if (searchTerm.trim().length < minSearchInput && !(fetchOnOpen && searchTerm === '')) {
        setSearchResults(resolvedInitialOptions);
        setIsSearching(false);
        return;
      }

      setIsSearching(true);
      try {
        const results = await onSearch(searchTerm);
        setSearchResults(results);
      } catch (error) {
        console.error('Search failed:', error);
        setSearchResults([]);
      }
      setIsSearching(false);
    },
    [onSearch, value, resolvedInitialOptions, minSearchInput, fetchOnOpen],
  );

  const debouncedSearch = useMemo(() => debounce(performSearch, debounceMs), [performSearch, debounceMs]);

  const handleSelect = async (currentValue: string) => {
    if (currentValue === 'add' && onAdd) {
      try {
        const option = await onAdd();
        onChange(option.value);
        setSearchValue(option.label);
        setOpen(false);
      } catch (error) {
        console.error('Error adding option:', error);
      }
      return;
    }

    const selectedOption = searchResults?.find((option) => option.value === currentValue);
    if (selectedOption && !selectedOption.disabled) {
      onChange(currentValue === value ? '' : currentValue);
      setSearchValue(selectedOption.label);
      setOpen(false);
    }
  };

  const handleSearchChange = (nextValue: string) => {
    isUserTypingRef.current = true;
    setSearchValue(nextValue);
    if (clearValueOnType) {
      onChange('');
    }
    setOpen(true);

    if (!onSearch) {
      setSearchResults(filterStaticOptions(nextValue));
    } else {
      debouncedSearch(nextValue);
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    // Prevent the popover from stealing focus from the input
    e.preventDefault();
    e.stopPropagation();
    setOpen(true);

    if (fetchOnOpen && onSearch && searchResults.length === 0 && !searchValue && !isSearching) {
      performSearch('');
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    // Prevent the popover from stealing focus from the input
    e.stopPropagation();
    e.preventDefault();
    setOpen(true);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onChange('');
    setSearchValue('');
    setSearchResults(fetchOnOpen ? [] : filterStaticOptions(''));
    setOpen(false);
    onClear?.();
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <Command className="overflow-visible" shouldFilter={false}>
        <PopoverTrigger asChild>
          <PopoverAnchor>
            <div
              className={inputContainer({ className: `items-center gap-2 px-3 ${className ?? ''}` })}
              onClick={() => inputRef.current?.focus()}
            >
              <FontAwesomeIcon className="size-4 shrink-0 opacity-50" icon={faSearch} />
              <CommandPrimitive.Input
                {...inputProps}
                className="flex-1 min-w-0 bg-transparent text-sm placeholder:text-muted-foreground outline-hidden disabled:cursor-not-allowed disabled:opacity-50"
                aria-autocomplete="list"
                aria-expanded={open}
                aria-haspopup="listbox"
                disabled={disabled}
                onClick={handleClick}
                onFocus={handleFocus}
                onValueChange={handleSearchChange}
                placeholder={placeholder}
                ref={inputRef}
                role="combobox"
                value={searchValue}
              />
              {searchValue && !disabled && (
                <button
                  className="cursor-pointer text-content-tertiary hover:text-content-secondary"
                  onClick={handleClear}
                  type="button"
                >
                  <FontAwesomeIcon className="size-4" icon={faXmark} />
                </button>
              )}
            </div>
          </PopoverAnchor>
        </PopoverTrigger>
        <PopoverContent
          className="p-0"
          align="start"
          disablePortal={disablePopoverPortal}
          onOpenAutoFocus={(e) => {
            // Prevent popover from stealing focus from the input
            e.preventDefault();
          }}
          side="bottom"
          // Slightly larger than the default so the dropdown doesn't visually
          // touch the input's focus-within ring.
          sideOffset={6}
          // Match the popover width to the input, or allow it to grow with content
          style={popoverAutoWidth
            ? { minWidth: 'var(--radix-popover-trigger-width)', width: 'max-content' }
            : { width: 'var(--radix-popover-trigger-width)' }
          }
        >
          {isSearching ? (
            <div className="flex items-center justify-center p-4">
              <LoadingIndicator size="sm" />
              <span className="ml-2 text-sm text-slate-500">Searching...</span>
            </div>
          ) : (
            <CommandList>
              <CommandEmpty>
                {onSearch && searchValue.trim().length < minSearchInput ? placeholder : searchEmptyMessage}
              </CommandEmpty>
              {(searchResults.length > 0 || onAdd) && (
                <CommandGroup>
                  {searchResults.map((option) => (
                    <CommandItem
                      disabled={option.disabled}
                      key={option.value}
                      onSelect={() => handleSelect(option.value)}
                      value={option.value}
                    >
                      <FlexContainer className="w-full gap-0.5" direction="column">
                        <FlexContainer className="w-full">
                          {option.label}
                          <FontAwesomeIcon
                            className={`ml-auto h-4 w-4 ${option.value === value ? 'opacity-100' : 'opacity-0'}`}
                            icon={faCheck}
                          />
                        </FlexContainer>
                        {option.subtext && <Body size="sm">{option.subtext}</Body>}
                      </FlexContainer>
                    </CommandItem>
                  ))}
                  {onAdd && (
                    <CommandItem onSelect={() => handleSelect('add')} value="add">
                      <FlexContainer className="w-full gap-2" items="center">
                        <FontAwesomeIcon className="text-content-secondary" icon={faPlus} />
                        {addButtonText}
                      </FlexContainer>
                    </CommandItem>
                  )}
                </CommandGroup>
              )}
            </CommandList>
          )}
        </PopoverContent>
      </Command>
    </Popover>
  );
}
